import apisauce from 'apisauce';

import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import { refreshImpersonationSession } from '@/shared/services/data/user-data/impersonation-refresh.data';
import { getSalesTeamMemberDetailsById } from '@/shared/services/data/sales-management/sales-management.data';
import { userRefreshToken } from '@/shared/services/data/user-data/auth.data';
import {
  cleanupUserData,
  getSessionCorrelationId,
  getUserAccessToken,
  getUserRefreshToken,
  isUserSalesTeam,
  setSessionCorrelationId,
  setUserTeam,
  setUserToken
} from '@/shared/services/helpers/auth-helper/auth-helper.service';
import {
  attachImpersonationHeader,
  getImpersonationRefreshToken,
  getImpersonationSessionId,
  isImpersonating,
  isMutatingHttpMethod,
  requestImpersonationMutationConfirm
} from '@/shared/services/helpers/impersonation/impersonation-helper.service';

import { SERVER_BASE_URL } from './routes-config';

// Deduplicates concurrent refresh calls: the first 401 creates the promise,
// subsequent concurrent 401s await the same one instead of firing N requests.
let refreshPromise: Promise<Awaited<ReturnType<typeof userRefreshToken>>> | null = null;

// Prevents N concurrent failed-refresh callers from all firing window.location.replace.
let isRedirecting = false;

const redirectToLogin = () => {
  if (isRedirecting || window.location.pathname.startsWith('/auth/')) return;
  isRedirecting = true;
  const target = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.replace(`/auth/login?target=${target}`);
};

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const create = (baseURL = SERVER_BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'X-Client-Platform': 'internal_admin',
      'X-Client-Type': 'web',
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      'Accept-Language': '*'
    },
    timeout: 240000
  });
  api.addRequestTransform(async (request) => {
    request.headers = request.headers || {};
    request.httpsAgent = {
      rejectUnauthorized: false
    };
    let sessionCorrelationId = getSessionCorrelationId();
    if (!sessionCorrelationId) {
      sessionCorrelationId = createSessionId();
      setSessionCorrelationId(sessionCorrelationId);
    }
    request.headers['x-session-id'] = sessionCorrelationId;

    const jwt = getUserAccessToken();
    if (jwt) request.headers.Authorization = `Bearer ${jwt}`;
    attachImpersonationHeader(request.headers as Record<string, string>);

    const method = (request.method || 'get').toLowerCase();
    const url = request.url || '';
    const skipConfirm = url.includes('/auth/impersonation/') || url.includes('/auth/refreshToken');

    if (isImpersonating() && isMutatingHttpMethod(method, url) && !skipConfirm) {
      const allowed = await requestImpersonationMutationConfirm({ method, url });
      if (!allowed) {
        throw new Error('IMPERSONATION_MUTATION_CANCELLED');
      }
    }
  });
  api.addAsyncResponseTransform(async (response) => {
    if (response?.headers && response?.headers['x-session-id']) {
      setSessionCorrelationId(response?.headers['x-session-id']);
    }
    if (
      response.status === 401 &&
      response.config?.url &&
      response.config?.url?.indexOf('/auth/refreshToken') === -1 &&
      response.config?.url?.indexOf('/auth/impersonation/refreshToken') === -1
    ) {
      if (isImpersonating()) {
        const sessionId = getImpersonationSessionId();
        const impersonationRefreshToken = getImpersonationRefreshToken();
        if (sessionId && impersonationRefreshToken) {
          if (!refreshPromise) {
            refreshPromise = refreshImpersonationSession(sessionId, impersonationRefreshToken)
              .then((data) => ({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                tokenExpiresIn: data.tokenExpiresIn,
                userId: data.userId,
                email: data.email,
                name: data.name,
                role: data.role
              }))
              .finally(() => {
                refreshPromise = null;
              });
          }
          const userData = await refreshPromise;
          if (userData && 'accessToken' in userData) {
            setUserToken({
              ...userData,
              refreshToken: impersonationRefreshToken
            });
            if (userData.userId && isUserSalesTeam()) {
              const teamData = await getSalesTeamMemberDetailsById(
                userData.userId,
                'DASHBOARD_MODULE'
              );
              if (!isErrorInfo(teamData) && teamData?.data?.team) {
                setUserTeam(teamData.data.team);
              }
            }
            const accessToken = userData.accessToken;
            if (accessToken) {
              api.setHeader('Authorization', `Bearer ${accessToken}`);
              const data = await api.any(response.config as any);
              response.data = data.data;
              response.status = data.status;
              response.problem = data.problem;
              response.headers = data.headers;
              response.ok = data.ok;
            }
          }
          return;
        }
      }

      const refreshToken = getUserRefreshToken();
      if (refreshToken) {
        if (!refreshPromise) {
          refreshPromise = userRefreshToken(refreshToken).finally(() => {
            refreshPromise = null;
          });
        }
        const userData = await refreshPromise;

        if (isErrorInfo(userData)) {
          // Only cleanup on actual authentication failures (401/403), not network errors
          // Network errors like TIMEOUT_ERROR, NETWORK_ERROR have status === undefined
          if (userData.status === 401 || userData.status === 403) {
            cleanupUserData();
            redirectToLogin();
          }
          // Don't retry if it's a network error - user should stay logged in
          return;
        }

        setUserToken({ ...userData, refreshToken });

        if (userData.userId && isUserSalesTeam()) {
          const teamData = await getSalesTeamMemberDetailsById(userData.userId, 'DASHBOARD_MODULE');
          if (isErrorInfo(teamData)) {
            console.log('no team detected');
          } else {
            if (teamData?.data?.team) {
              setUserTeam(teamData?.data?.team);
            }
          }
        }

        const accessToken = userData.accessToken;
        if (accessToken) {
          api.setHeader('Authorization', `Bearer ${accessToken}`);
          // retry original request
          const data = await api.any(response.config as any);
          // replace response data
          response.data = data.data;
          response.status = data.status;
          response.problem = data.problem;
          response.headers = data.headers;
          response.ok = data.ok;
        }
      } else {
        cleanupUserData();
        redirectToLogin();
      }
    }
  });

  return api;
};

const api = create();

export default api;
export type ApiType = typeof api;
